import { z } from 'zod';

const MilestoneName = z.string().min(1).max(120);
const ColorHex = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/**
 * Computed status — not stored.  Service derives from dates + isCompleted +
 * aggregated task progress.
 *
 *   completed  — isCompleted = true
 *   overdue    — planned_end_date past + not completed + progress < 100
 *   active     — between planned_start_date and planned_end_date
 *   at_risk    — within 7 days of end + progress < 50%
 *   planned    — start in the future, or no dates
 */
export const MilestoneStatusSchema = z.enum([
  'planned',
  'active',
  'at_risk',
  'completed',
  'overdue',
]);
export type MilestoneStatus = z.infer<typeof MilestoneStatusSchema>;

export const MilestoneSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  name: z.string(),
  color: z.string(),
  plannedStartDate: z.string().nullable(),
  plannedEndDate: z.string().nullable(),
  isCompleted: z.boolean(),
  completedAt: z.string().nullable(),
  orderIndex: z.number().int().nonnegative(),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Computed: count of tasks pointing at this milestone. */
  tasksTotal: z.number().int().nonnegative(),
  /** Computed: count of those tasks with done-category status. */
  tasksCompleted: z.number().int().nonnegative(),
  /** Computed: 0-100 percentage from the two counts (0 when total = 0). */
  progressPct: z.number().int().min(0).max(100),
  /** Computed: see MilestoneStatusSchema. */
  status: MilestoneStatusSchema,
  // ── Schedule-integrity rollup (additive, all client-default-safe) ─────
  //
  // `effective*` reflect the dates derived from LINKED TASKS — the
  // earliest task.startDate and the latest task.dueDate among non-
  // archived linked tasks.  If no linked task has a start (or due),
  // the corresponding effective date is null.
  //
  // The three counts surface mismatches between the planned window and
  // the actual schedule: tasks scheduled to start before the plan
  // (`tasksEarly`), tasks scheduled to end after the plan (`tasksLate`),
  // and tasks past their own deadline today (`tasksOverdue`).  Status
  // itself stays a pure plan-level signal — these are reported as
  // separate "schedule risk" badges on the UI surfaces.
  effectiveStartDate: z.string().nullable().default(null),
  effectiveEndDate: z.string().nullable().default(null),
  tasksOverdue: z.number().int().nonnegative().default(0),
  tasksEarly: z.number().int().nonnegative().default(0),
  tasksLate: z.number().int().nonnegative().default(0),
});
export type MilestoneProjection = z.infer<typeof MilestoneSchema>;

export const MilestoneListSchema = z.array(MilestoneSchema);

/**
 * Team-scoped milestone projection — extends the per-project shape with
 * the project's display fields so the cross-project timeline can group
 * lanes / colour-code bars without a second round-trip.
 */
export const TeamMilestoneSchema = MilestoneSchema.extend({
  projectName: z.string(),
  projectSlug: z.string(),
  projectColor: z.string().nullable(),
});
export type TeamMilestoneProjection = z.infer<typeof TeamMilestoneSchema>;
export const TeamMilestoneListSchema = z.array(TeamMilestoneSchema);

export const CreateMilestoneSchema = z
  .object({
    name: MilestoneName,
    color: ColorHex.optional(),
    plannedStartDate: DateOnly.nullable().optional(),
    plannedEndDate: DateOnly.nullable().optional(),
  })
  .strict();
export type CreateMilestoneInput = z.infer<typeof CreateMilestoneSchema>;

export const UpdateMilestoneSchema = z
  .object({
    name: MilestoneName.optional(),
    color: ColorHex.optional(),
    plannedStartDate: DateOnly.nullable().optional(),
    plannedEndDate: DateOnly.nullable().optional(),
    isCompleted: z.boolean().optional(),
    orderIndex: z.number().int().nonnegative().optional(),
  })
  .strict();
export type UpdateMilestoneInput = z.infer<typeof UpdateMilestoneSchema>;

// Task ↔ milestone link
export const SetTaskMilestoneSchema = z
  .object({
    milestoneId: z.string().uuid().nullable(),
  })
  .strict();
export type SetTaskMilestoneInput = z.infer<typeof SetTaskMilestoneSchema>;
